import React, { FC, useMemo } from "react";

import { usePagedRows } from "../../../components/pagination/usePagedRows";
import { exportDataToExcel } from "../../../utils/ExportData";
import { askTable } from "../ask/reportAsk.utils";
import {
  profitabilityByClient,
  realization,
  realizationTone,
  TONE_BAR,
  TONE_TEXT,
} from "../moreReports.utils";
import { formatHours, formatMoney, presetRange } from "../reports.utils";
import {
  DateMenu,
  PeopleClientTypeFilters,
  useReportFilters,
} from "../ui/ReportFilters";
import {
  Avatar,
  Card,
  Empty,
  Kpi,
  KpiGrid,
  LoadError,
  Loading,
  MiniBar,
  Note,
  NUM,
  ReportPage,
  TableBox,
  TD,
  TH,
  useFocusRows,
} from "../ui/ReportUi";
import { useAllInvoices, useAllTime } from "../useReportData";

const TABLE = "Profitability by client";

/** Client profitability (2026-11-05): value of time worked vs. billed and paid, per client. Billing access only. */
export const ProfitabilityReport: FC = () => {
  const state = useReportFilters("thisYear");
  const { filters } = state;
  const range = useMemo(() => presetRange(filters.preset), [filters.preset]);
  const time = useAllTime({
    from: range?.from ?? null,
    to: range?.to ?? null,
    userIds: filters.userIds,
    customerIds: filters.clientIds,
    workTypeIds: filters.workTypeIds,
  });
  const invoices = useAllInvoices({
    from: range?.from ?? null,
    to: range?.to ?? null,
    customerIds: filters.clientIds,
    userIds: filters.userIds,
  });
  const isLoading = time.isLoading || invoices.isLoading;
  const rows = useMemo(
    () => profitabilityByClient(time.data, invoices.data),
    [time.data, invoices.data],
  );
  const hours = rows.reduce((s, r) => s + r.hours, 0);
  const value = rows.reduce((s, r) => s + r.value, 0);
  const billed = rows.reduce((s, r) => s + r.billed, 0);
  const paid = rows.reduce((s, r) => s + r.paid, 0);
  const firmPct = realization(billed, value);
  const under70 = rows.filter(
    (r) => r.realization !== null && r.realization < 70,
  );
  const v = (x: React.ReactNode) => (isLoading ? "…" : x);

  const shown = useFocusRows(TABLE, rows, (r) => r.key);
  const { pageRows, pager } = usePagedRows(shown);

  const onExport = () =>
    exportDataToExcel(
      rows.map((r) => ({
        Client: r.name,
        Hours: Math.round(r.hours * 100) / 100,
        "Value of time": Math.round(r.value),
        Billed: Math.round(r.billed),
        Paid: Math.round(r.paid),
        "Realization (%)": r.realization ?? "",
      })),
      "Client profitability",
    );

  const ask = useMemo(
    () => ({
      title: "Client profitability",
      path: "/reports/profitability",
      description:
        "Per client: billable hours, their value at each person's rate, billed and paid. Realization = billed ÷ value.",
      kpis: [
        { label: "Value of time", value: formatMoney(value) },
        { label: "Billed", value: formatMoney(billed) },
        { label: "Paid", value: formatMoney(paid) },
        { label: "Realization", value: firmPct === null ? "—" : `${firmPct}%` },
      ],
      tables: [
        askTable(
          TABLE,
          rows,
          [
            {
              key: "client",
              label: "Client",
              kind: "text",
              value: (r) => r.name,
            },
            {
              key: "hours",
              label: "Hours",
              kind: "hours",
              value: (r) => r.hours,
            },
            {
              key: "value",
              label: "Value of time",
              kind: "money",
              value: (r) => r.value,
            },
            {
              key: "billed",
              label: "Billed",
              kind: "money",
              value: (r) => r.billed,
            },
            { key: "paid", label: "Paid", kind: "money", value: (r) => r.paid },
            {
              key: "realization",
              label: "Realization",
              kind: "percent",
              value: (r) => r.realization,
            },
          ],
          (r) => r.key,
        ),
      ],
    }),
    [rows, value, billed, paid, firmPct],
  );

  return (
    <ReportPage
      title={"Client profitability"}
      description={
        "For each client: hours worked, their value at each person's rate, what was billed and what was paid. Only people with billing access see this report."
      }
      onExport={rows.length ? onExport : undefined}
      ask={isLoading ? undefined : ask}
      askSuggestions={[
        "Which clients are we under-billing?",
        "Who has the best realization?",
        "How much is still owed?",
      ]}
      filters={
        <>
          <DateMenu
            value={filters.preset}
            onChange={(preset) => state.setFilters({ preset })}
          />
          <PeopleClientTypeFilters state={state} />
        </>
      }>
      {(time.isError || invoices.isError) && <LoadError />}
      <KpiGrid>
        <Kpi
          label={"Value of time"}
          value={v(formatMoney(value))}
          note={`${formatHours(hours)} worked`}
        />
        <Kpi
          label={"Billed"}
          value={v(formatMoney(billed))}
          note={
            firmPct === null
              ? "No value of time yet"
              : `Realization ${firmPct}%`
          }
        />
        <Kpi
          label={"Paid"}
          value={v(formatMoney(paid))}
          note={`${formatMoney(Math.max(0, billed - paid))} still owed`}
        />
        <Kpi
          label={"Clients under 70%"}
          value={v(under70.length)}
          note={"Billed less than 70% of the value"}
          tone={under70.length ? "red" : "default"}
        />
      </KpiGrid>

      <Card title={TABLE}>
        {isLoading ? (
          <Loading />
        ) : !rows.length ? (
          <Empty text={"No time or invoices for these dates."} />
        ) : (
          <TableBox pager={pager}>
            <thead>
              <tr>
                <th scope={"col"} className={TH}>
                  Client
                </th>
                <th scope={"col"} className={`${TH} ${NUM}`}>
                  Hours
                </th>
                <th scope={"col"} className={`${TH} ${NUM}`}>
                  Value of time
                </th>
                <th scope={"col"} className={`${TH} ${NUM}`}>
                  Billed
                </th>
                <th scope={"col"} className={`${TH} ${NUM}`}>
                  Paid
                </th>
                <th scope={"col"} className={`${TH} ${NUM}`}>
                  Realization
                </th>
                <th scope={"col"} className={TH}>
                  Billed vs. value
                </th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((r) => {
                const tone = realizationTone(r.realization);
                return (
                  <tr key={r.key}>
                    <td className={`${TD} font-bold`}>
                      <Avatar name={r.name} square />
                      {r.name}
                    </td>
                    <td className={`${TD} ${NUM}`}>{formatHours(r.hours)}</td>
                    <td className={`${TD} ${NUM}`}>{formatMoney(r.value)}</td>
                    <td className={`${TD} ${NUM}`}>{formatMoney(r.billed)}</td>
                    <td className={`${TD} ${NUM}`}>{formatMoney(r.paid)}</td>
                    <td
                      className={`${TD} ${NUM} font-extrabold`}
                      style={{ color: TONE_TEXT[tone] }}>
                      {r.realization === null ? "—" : `${r.realization}%`}
                    </td>
                    <td className={TD}>
                      <MiniBar
                        pct={r.realization ?? 0}
                        color={TONE_BAR[tone]}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </TableBox>
        )}
      </Card>

      <Note>
        Realization = billed ÷ value of time. Below 85% shows in orange, below
        70% in red. Hours and value count billable time only, at each
        person&apos;s current hourly rate; billed and paid are the invoices
        dated in the chosen dates (paid = total minus what is still owed).
      </Note>
    </ReportPage>
  );
};
